// Definición de esquemas para las plantillas ETL.
// Cada campo define:
// - key: nombre técnico del campo (así lo espera el backend en el JSON)
// - label: etiqueta legible
// - type: tipo de dato esperado
// - required: si es obligatorio
// - options: valores aceptados (solo para enums/listas)
// - example: valor de ejemplo para la plantilla
// - notes: nota adicional visible en la plantilla

export interface FieldSchema {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'email' | 'phone' | 'enum';
  required: boolean;
  options?: string[];
  example: string | number;
  notes?: string;
}

export interface EntitySchema {
  id: string;
  name: string;
  description: string;
  icon: string;
  fields: FieldSchema[];
}

export const ETL_SCHEMAS: Record<string, EntitySchema> = {
  clients: {
    id: 'clients',
    name: 'Clientes',
    description: 'Registros de clientes con datos de contacto',
    icon: '👤',
    fields: [
      { key: 'name', label: 'Nombre completo', type: 'text', required: true, example: 'María González López', notes: 'Mínimo 2 caracteres, máximo 100' },
      { key: 'email', label: 'Correo electrónico', type: 'email', required: true, example: 'maria.gonzalez@email.com', notes: 'Formato válido de correo' },
      { key: 'phone', label: 'Teléfono', type: 'phone', required: false, example: '8112345678', notes: 'Solo dígitos, 10 a 15 caracteres' },
      { key: 'address', label: 'Dirección', type: 'text', required: false, example: 'Av. Constitución 1234, Col. Centro, Monterrey, NL', notes: 'Dirección completa, texto libre' },
      { key: 'company', label: 'Empresa', type: 'text', required: false, example: 'Constructora del Norte SA de CV', notes: 'Nombre de la empresa (si aplica)' },
      { key: 'tax_id', label: 'RFC', type: 'text', required: false, example: 'GOLM850101XXX', notes: 'RFC con homoclave (13 caracteres)' },
    ],
  },

  projects: {
    id: 'projects',
    name: 'Proyectos',
    description: 'Registros de proyectos de construcción',
    icon: '📋',
    fields: [
      { key: 'name', label: 'Nombre del proyecto', type: 'text', required: true, example: 'Casa Lumina', notes: 'Mínimo 2 caracteres' },
      { key: 'clientEmail', label: 'Email del cliente', type: 'email', required: true, example: 'maria.gonzalez@email.com', notes: 'Debe coincidir con un cliente ya registrado' },
      { key: 'description', label: 'Descripción', type: 'text', required: false, example: 'Residencia unifamiliar de 2 pisos', notes: 'Texto libre' },
      { key: 'budget', label: 'Presupuesto', type: 'number', required: true, example: 1250000, notes: 'Número mayor o igual a 0' },
      { key: 'address', label: 'Dirección de la obra', type: 'text', required: false, example: 'Calle Padua 106, Col. Gonzalitos, Monterrey, NL', notes: 'Dirección completa' },
      { key: 'status', label: 'Estado', type: 'enum', required: false, options: ['planning', 'in_progress', 'on_hold', 'completed', 'cancelled'], example: 'planning', notes: 'Valores permitidos' },
    ],
  },

  payments: {
    id: 'payments',
    name: 'Pagos',
    description: 'Registros de pagos recibidos de clientes',
    icon: '💰',
    fields: [
      { key: 'id', label: 'ID externo', type: 'text', required: false, example: 'PAG-0001', notes: 'ID del sistema anterior' },
      { key: 'projectName', label: 'Nombre del proyecto', type: 'text', required: true, example: 'Casa Lumina', notes: 'Debe coincidir con un proyecto ya registrado' },
      { key: 'amount', label: 'Monto', type: 'number', required: true, example: 25000, notes: 'Número mayor a 0' },
      { key: 'date', label: 'Fecha del pago', type: 'date', required: true, example: '2024-03-15', notes: 'Formato YYYY-MM-DD' },
      { key: 'concept', label: 'Concepto', type: 'text', required: true, example: 'Pago semanal semana 12', notes: 'Mínimo 2 caracteres' },
      { key: 'method', label: 'Método de pago', type: 'enum', required: false, options: ['transfer', 'cash', 'card', 'check'], example: 'transfer', notes: 'Valores permitidos' },
      { key: 'reference', label: 'Referencia', type: 'text', required: false, example: 'TRF-20240315-001', notes: 'Folio o referencia bancaria' },
    ],
  },

  expenses: {
    id: 'expenses',
    name: 'Gastos del Proyecto',
    description: 'Gastos operativos y de proyecto (esquema extendido)',
    icon: '💳',
    fields: [
      { key: 'external_id', label: 'ID Externo', type: 'text', required: false, example: '437265956250000065', notes: 'ID original del sistema anterior para deduplicación' },
      { key: 'entry_date', label: 'Fecha del Documento', type: 'date', required: true, example: '2022-09-07', notes: 'Formato YYYY-MM-DD' },
      { key: 'project_name', label: 'Nombre del Proyecto', type: 'text', required: false, example: 'Casa Lumina', notes: 'Debe existir en Projects si aplica' },
      { key: 'category_name', label: 'Rubro de Gasto', type: 'text', required: true, example: 'Materiales', notes: 'Debe existir en Catálogos > Rubros de Gasto' },
      { key: 'department_name', label: 'Área', type: 'text', required: false, example: 'Obra', notes: 'Debe existir en Catálogos > Departamentos. Si se omite, se hereda del rubro.' },
      { key: 'source_code', label: 'Cuenta Origen', type: 'text', required: false, example: '01 EFVO', notes: 'Código de Catálogos > Cuentas de Origen' },
      { key: 'flow_type', label: 'Tipo de Flujo', type: 'enum', required: false, options: ['direct_expense', 'internal_transfer', 'cash_expense'], example: 'direct_expense', notes: 'Si es internal_transfer NO se registra como gasto' },
      { key: 'concept', label: 'Concepto', type: 'text', required: true, example: 'Compra de cemento Portland', notes: 'Mínimo 2 caracteres' },
      { key: 'fiscal_reference', label: 'Referencia Fiscal', type: 'text', required: false, example: 'HRP6908', notes: 'Número de factura, ticket o remisión' },
      { key: 'supplier_rfc', label: 'RFC Proveedor', type: 'text', required: false, example: 'CEM850101XXX', notes: 'RFC con homoclave' },
      { key: 'week_reference', label: 'Referencia Semanal', type: 'text', required: false, example: 'S36-19', notes: 'Formato S##-## o S##/AAAA' },
      { key: 'amount', label: 'Importe', type: 'number', required: true, example: 8500, notes: 'Número mayor a 0' },
      { key: 'input_date', label: 'Fecha de Captura', type: 'date', required: false, example: '2022-09-18', notes: 'Fecha en que se capturó en el sistema anterior' },
      { key: 'input_time', label: 'Hora de Captura', type: 'text', required: false, example: '14:17:00', notes: 'Formato HH:MM:SS' },
      { key: 'details', label: 'Detalles', type: 'text', required: false, example: 'SEMANA 36-19', notes: 'Texto libre original de COMENTARIOS' },
    ],
  },

  'expenses-manual': {
    id: 'expenses-manual',
    name: 'Egresos Manuales',
    description: 'Egresos capturados manualmente (libro contable paralelo)',
    icon: '📤',
    fields: [
      { key: 'external_id', label: 'ID Externo', type: 'text', required: false, example: '437265956250000065', notes: 'ID original del sistema anterior' },
      { key: 'entry_date', label: 'Fecha', type: 'date', required: true, example: '2022-09-07', notes: 'Formato YYYY-MM-DD' },
      { key: 'folio', label: 'Folio', type: 'text', required: false, example: 'EGR-2024-001', notes: 'Folio interno' },
      { key: 'document_number', label: 'No. Documento', type: 'text', required: false, example: 'FAC-001', notes: 'Número de factura o documento' },
      { key: 'beneficiary_type', label: 'Tipo de Beneficiario', type: 'enum', required: false, options: ['office', 'supplier', 'client', 'contractor'], example: 'supplier', notes: 'Opciones: office, supplier, client, contractor' },
      { key: 'beneficiary_name', label: 'Beneficiario', type: 'text', required: false, example: 'Cemex SA de CV', notes: 'Nombre o razón social' },
      { key: 'concept_name', label: 'Concepto', type: 'text', required: true, example: 'Compra de material', notes: 'Debe existir en Catálogos > Conceptos. Si no existe, se crea automáticamente.' },
      { key: 'category_name', label: 'Rubro de Gasto', type: 'text', required: false, example: 'Materiales', notes: 'Debe existir en Catálogos > Rubros de Gasto' },
      { key: 'department_name', label: 'Área', type: 'text', required: false, example: 'Obra', notes: 'Debe existir en Catálogos > Departamentos' },
      { key: 'source_code', label: 'Cuenta Origen', type: 'text', required: false, example: '02 SPEI', notes: 'Código de Catálogos > Cuentas de Origen' },
      { key: 'flow_type', label: 'Tipo de Flujo', type: 'enum', required: false, options: ['direct', 'internal_transfer'], example: 'direct', notes: 'Valores permitidos' },
      { key: 'project_name', label: 'Proyecto', type: 'text', required: false, example: 'Casa Lumina', notes: 'Nombre exacto del proyecto (opcional)' },
      { key: 'fiscal_reference', label: 'Referencia Fiscal', type: 'text', required: false, example: 'JI 1306858', notes: 'Número de factura o ticket' },
      { key: 'movement_type_name', label: 'Tipo de Movimiento', type: 'text', required: false, example: 'Transferencia', notes: 'Debe coincidir con un tipo de movimiento' },
      { key: 'movement_detail_name', label: 'Detalle de Movimiento', type: 'text', required: false, example: 'Pago a proveedor', notes: 'Debe coincidir con un detalle de movimiento' },
      { key: 'amount', label: 'Importe', type: 'number', required: true, example: 8500, notes: 'Número mayor a 0' },
      { key: 'week_reference', label: 'Referencia Semanal', type: 'text', required: false, example: 'S36-19', notes: 'Formato S##-## o S##/AAAA' },
      { key: 'input_date', label: 'Fecha de Captura', type: 'date', required: false, example: '2022-09-18', notes: 'Fecha en que se capturó en el sistema anterior' },
      { key: 'input_time', label: 'Hora de Captura', type: 'text', required: false, example: '14:17:00', notes: 'Formato HH:MM:SS' },
      { key: 'details', label: 'Detalles', type: 'text', required: false, example: 'SEMANA 36-19', notes: 'Texto libre' },
    ],
  },

  incomes: {
    id: 'incomes',
    name: 'Ingresos (manual)',
    description: 'Ingresos capturados manualmente (libro paralelo a facturas)',
    icon: '📥',
    fields: [
      { key: 'id', label: 'ID externo', type: 'text', required: false, example: 'ING-0001', notes: 'ID del sistema anterior para deduplicación' },
      { key: 'folio', label: 'Folio', type: 'text', required: false, example: 'ING-2024-001', notes: 'Folio interno' },
      { key: 'entry_date', label: 'Fecha', type: 'date', required: true, example: '2024-03-15', notes: 'Formato YYYY-MM-DD' },
      { key: 'document_number', label: 'No. Documento', type: 'text', required: false, example: 'REC-001', notes: 'Número de recibo o documento' },
      { key: 'beneficiary_type', label: 'Tipo de beneficiario', type: 'enum', required: false, options: ['office', 'supplier', 'client', 'contractor'], example: 'client', notes: 'Opciones: office, supplier, client, contractor' },
      { key: 'beneficiary_name', label: 'Nombre del beneficiario', type: 'text', required: false, example: 'María González', notes: 'Nombre o razón social' },
      { key: 'concept_name', label: 'Concepto', type: 'text', required: true, example: 'Pago de cliente', notes: 'Debe coincidir con un concepto registrado en Catálogos' },
      { key: 'project_name', label: 'Proyecto', type: 'text', required: false, example: 'Casa Lumina', notes: 'Nombre exacto del proyecto (opcional)' },
      { key: 'movement_type_name', label: 'Tipo de movimiento', type: 'text', required: false, example: 'Efectivo', notes: 'Debe coincidir con un tipo de movimiento' },
      { key: 'movement_detail_name', label: 'Detalle de movimiento', type: 'text', required: false, example: 'Pago de cliente', notes: 'Debe coincidir con un detalle de movimiento' },
      { key: 'amount', label: 'Importe', type: 'number', required: true, example: 25000, notes: 'Número mayor a 0' },
      { key: 'details', label: 'Detalles', type: 'text', required: false, example: 'Pago correspondiente a semana 12', notes: 'Texto libre' },
    ],
  },

  suppliers: {
    id: 'suppliers',
    name: 'Proveedores',
    description: 'Catálogo de proveedores del despacho',
    icon: '🏭',
    fields: [
      { key: 'name', label: 'Nombre / Razón social', type: 'text', required: true, example: 'Cemex SA de CV', notes: 'Nombre completo o razón social' },
      { key: 'rfc', label: 'RFC', type: 'text', required: false, example: 'CEM850101XXX', notes: 'RFC con homoclave (13 caracteres)' },
      { key: 'email', label: 'Email', type: 'email', required: false, example: 'contacto@cemex.com', notes: 'Correo de contacto' },
      { key: 'phone', label: 'Teléfono', type: 'phone', required: false, example: '8183456789', notes: 'Solo dígitos' },
      { key: 'contact_name', label: 'Persona de contacto', type: 'text', required: false, example: 'Juan Pérez', notes: 'Nombre del vendedor o contacto' },
      { key: 'address', label: 'Dirección', type: 'text', required: false, example: 'Av. Lázaro Cárdenas 2400, Monterrey, NL', notes: 'Dirección completa' },
      { key: 'category', label: 'Categoría', type: 'text', required: false, example: 'Materiales de construcción', notes: 'Categoría del proveedor' },
      { key: 'notes', label: 'Notas', type: 'text', required: false, example: 'Proveedor preferido para cemento', notes: 'Notas adicionales' },
    ],
  },

  contractors: {
    id: 'contractors',
    name: 'Contratistas',
    description: 'Catálogo de contratistas y subcontratistas',
    icon: '🔨',
    fields: [
      { key: 'name', label: 'Nombre completo', type: 'text', required: true, example: 'Pedro Ramírez', notes: 'Nombre del contratista' },
      { key: 'rfc', label: 'RFC', type: 'text', required: false, example: 'RAMP850101XXX', notes: 'RFC con homoclave (13 caracteres)' },
      { key: 'email', label: 'Email', type: 'email', required: false, example: 'pedro.ramirez@email.com', notes: 'Correo de contacto' },
      { key: 'phone', label: 'Teléfono', type: 'phone', required: false, example: '8112345678', notes: 'Solo dígitos' },
      { key: 'specialty', label: 'Especialidad', type: 'text', required: false, example: 'Albañilería', notes: 'Especialidad o trade' },
      { key: 'contact_name', label: 'Persona de contacto', type: 'text', required: false, example: 'María Ramírez', notes: 'Si aplica' },
      { key: 'address', label: 'Dirección', type: 'text', required: false, example: 'Col. Centro, Monterrey, NL', notes: 'Dirección completa' },
      { key: 'notes', label: 'Notas', type: 'text', required: false, example: 'Disponible fines de semana', notes: 'Notas adicionales' },
    ],
  },

  departments: {
    id: 'departments',
    name: 'Departamentos / Áreas',
    description: 'Áreas del despacho para asignar gastos',
    icon: '🏢',
    fields: [
      { key: 'id', label: 'ID del departamento', type: 'text', required: false, example: 'dept-admin', notes: 'Si se omite, se genera automáticamente' },
      { key: 'name', label: 'Nombre del área', type: 'text', required: true, example: 'Operaciones / Obra', notes: 'Mínimo 2 caracteres' },
      { key: 'description', label: 'Descripción', type: 'text', required: false, example: 'Ejecución de proyectos', notes: 'Texto libre' },
    ],
  },

  expense_categories: {
    id: 'expense_categories',
    name: 'Rubros de Gasto',
    description: 'Catálogo de categorías contables para clasificar gastos',
    icon: '📂',
    fields: [
      { key: 'id', label: 'ID del rubro', type: 'text', required: false, example: 'ec-materiales', notes: 'Si se omite, se genera automáticamente' },
      { key: 'name', label: 'Nombre del rubro', type: 'text', required: true, example: 'Materiales', notes: 'Debe ser único' },
      { key: 'kind', label: 'Tipo', type: 'enum', required: true, options: ['proyecto', 'operativo', 'administrativo', 'fiscal', 'interno'], example: 'proyecto', notes: 'Clasificación del rubro' },
      { key: 'department_id', label: 'Departamento', type: 'text', required: false, example: 'dept-construction', notes: 'ID del departamento al que pertenece' },
      { key: 'department_name', label: 'Nombre del Departamento', type: 'text', required: false, example: 'Obra', notes: 'Alternativa a department_id si no se conoce el ID' },
    ],
  },

  payment_sources: {
    id: 'payment_sources',
    name: 'Cuentas de Origen',
    description: 'Catálogo de cuentas/cajas desde las que sale el dinero',
    icon: '🏦',
    fields: [
      { key: 'id', label: 'ID de la cuenta', type: 'text', required: false, example: 'ps-efvo', notes: 'Si se omite, se genera automáticamente' },
      { key: 'code', label: 'Código', type: 'text', required: true, example: '01 EFVO', notes: 'Código corto identificador, debe ser único' },
      { key: 'name', label: 'Nombre', type: 'text', required: true, example: 'Efectivo Caja', notes: 'Nombre legible' },
      { key: 'kind', label: 'Tipo', type: 'enum', required: true, options: ['cash', 'bank_transfer', 'debit_card', 'check', 'internal'], example: 'cash', notes: 'Valores permitidos' },
      { key: 'description', label: 'Descripción', type: 'text', required: false, example: 'Salida de caja chica', notes: 'Texto libre' },
    ],
  },
};

export function getSchemaForEntity(entity: string): EntitySchema | null {
  return ETL_SCHEMAS[entity] || null;
}